/**
 * The RigidBody2D/3D rules that need full scene context. Format validation (mass > 0,
 * enum values, vector format and so on) stays in each slice's linterParser.ts.
 */
import { armEmits, groundedArm, reportArm } from '../ruleArms.js';
import { dimSuffix } from './dim.js';
import { descendsFromClass } from '../../godot/classdb/classBaseTypes.js';
import { basisColumnScalesGodotFloat } from './basisColumnScales.js';
import { matchVector2 } from '../validators/vectorValidators.js';
import { ruleInt } from '../../godot/variant/intSlots.js';
import { boolSlotValue } from '../../godot/variant/variantBool.js';
/**
 * Per-axis scale tolerance: rigid_body_3d.cpp:667 and rigid_body_2d.cpp:648,
 * `Math::abs(scale.n - 1.0) > 0.05`. Not the pairwise uniformity test of
 * collision_object_3d.cpp:744 and collision_shape_3d.cpp:155: a uniform (2,2,2) trips
 * this one. A rigid-body tolerance with one reader, so it stays out of `godot/`.
 */
const RIGID_BODY_SCALE_TOLERANCE = 0.05;
/**
 * Node2D's own `scale` (node_2d.cpp:499), `(1, 1)` when absent, since the field
 * default (node_2d.h:39) and the serialised default agree. Not `transform`: Node2D's
 * `transform` carries `PROPERTY_USAGE_NONE` (node_2d.cpp:501) and is never written. Null when
 * the literal is malformed or a component narrows to a number the file does not state.
 */
function readScale2D(raw) {
    if (raw === undefined)
        return { x: 1, y: 1 };
    return matchVector2(raw);
}
export function makeRigidBodyLinterRule(dim) {
    const type = `RigidBody${dim}`;
    const prefix = `rigidbody${dimSuffix(dim)}`;
    // `_body_state_changed` reads the contact list, and everything
    // `max_contacts_reported` bounds, only inside `if (contact_monitor)`. Same
    // line number in both files, but they are two separate facts.
    const bodyFile = dim === '2D' ? 'rigid_body_2d.cpp' : 'rigid_body_3d.cpp';
    const contactMonitorCite = `${bodyFile}:181`;
    // `_sync_body_state`'s `contact_count = p_state->get_contact_count()`. Unlike the
    // guard above, this one does not coincide: the 3D body assigns an inverse inertia
    // tensor the 2D one has no counterpart for, putting the line one earlier.
    const countLine = dim === '2D' ? 155 : 154;
    const arms = {
        maxContactsWithoutMonitor: groundedArm(`${prefix}-max-contacts-without-monitor`, {
            kind: 'engine-inert',
            at: contactMonitorCite,
            unused: 'the colliding-bodies list and the contact signals live behind this guard',
        }),
        scaleOverriddenAtRuntime: groundedArm(`${prefix}-scale-overridden-at-runtime`, {
            kind: 'configuration-warning',
        }),
    };
    function check(context) {
        const diagnostics = [];
        const { node } = context;
        const rawProps = node.rawProperties;
        // `linear_damp` / `angular_damp` get no advisory: both hints (rigid_body_2d.cpp:763/767
        // "-1,100,0.001,or_greater", rigid_body_3d.cpp:785/789 "0,100,0.001,or_greater") leave
        // the high end open, and each low end is the setter's ERR_FAIL_COND bound (2D :425/:435
        // reject < -1, 3D :443/:453 reject < 0), which linterParser.ts reports as an error.
        // max_contacts_reported > 0 but contact_monitor is off. The flag gates the contact
        // list and the signals, not the count: `_sync_body_state` writes `contact_count`
        // unconditionally (:155, called at :179 ahead of the guard), and the server's
        // `can_report_contacts()` is `!contacts.is_empty()`, sized by max_contacts_reported.
        const maxContacts = ruleInt(rawProps.max_contacts_reported, 0);
        // The value, not the key: 0 is the default (rigid_body_2d.h:85, rigid_body_3d.h:82)
        // and sizes the list to nothing, and an unreadable literal never landed.
        if (maxContacts !== null && maxContacts > 0) {
            const contactMonitor = rawProps.contact_monitor;
            if (boolSlotValue(contactMonitor) !== true) {
                reportArm(diagnostics, arms.maxContactsWithoutMonitor, node, `${node.type} '${node.name}' sets max_contacts_reported while contact_monitor is off, ` +
                    'so get_colliding_bodies() stays empty and the body_entered/exited signals never ' +
                    'fire. The contact COUNT still works: _sync_body_state assigns contact_count at ' +
                    `${bodyFile}:${countLine}, before the contact_monitor guard at :181, and the physics server ` +
                    'gathers contacts on max_contacts_reported alone (can_report_contacts() is ' +
                    'contacts.is_empty() negated). Enable contact_monitor only if you need the list ' +
                    'or the signals.');
            }
        }
        // Per-axis scale the physics engine overrides at runtime (3D: rigid_body_3d.cpp:667,
        // with 2D below). The matcher reaches VehicleBody3D too.
        if (dim === '3D' && rawProps.transform !== undefined) {
            // The Godot-float, signed variant: rigid_body_3d.cpp:666's `abs(scale.axis - 1) > 0.05`
            // is true for an infinite column and false for a `nan` one, and comparing each
            // axis against 1.0 does not cancel `get_scale`'s shared `det_sign`.
            const scales = basisColumnScalesGodotFloat(rawProps.transform);
            if (scales) {
                const [sx, sy, sz] = scales;
                const overridden = Math.abs(sx - 1) > RIGID_BODY_SCALE_TOLERANCE ||
                    Math.abs(sy - 1) > RIGID_BODY_SCALE_TOLERANCE ||
                    Math.abs(sz - 1) > RIGID_BODY_SCALE_TOLERANCE;
                if (overridden) {
                    const shown = [sx, sy, sz].map((s) => Math.round(s * 1000) / 1000).join(', ');
                    reportArm(diagnostics, arms.scaleOverriddenAtRuntime, node, `${node.type} '${node.name}' has a scaled transform (${shown}). ` +
                        'Scale changes to RigidBody3D will be overridden by the physics engine when running. ' +
                        'Change the size in its children collision shapes instead.');
                }
            }
        }
        // Per-axis scale overridden at runtime (2D: rigid_body_2d.cpp:648,
        // `Math::abs(t.columns[n].length() - 1.0) > 0.05`). An unsigned column length is
        // `|scale.n|` whatever the rotation or skew, so reading `scale` needs no matrix.
        const scale2D = dim === '2D' ? readScale2D(rawProps.scale) : null;
        // PhysicalBone2D inherits this check (physical_bone_2d.cpp:109). A malformed
        // literal or a narrowed component gives `null` and no report, since no scale
        // may be quoted, and `v.vector2('scale')` on Node2D checks the shape.
        if (scale2D !== null) {
            const sx = Math.abs(scale2D.x);
            const sy = Math.abs(scale2D.y);
            if (Math.abs(sx - 1) > RIGID_BODY_SCALE_TOLERANCE || Math.abs(sy - 1) > RIGID_BODY_SCALE_TOLERANCE) {
                reportArm(diagnostics, arms.scaleOverriddenAtRuntime, node, `${node.type} '${node.name}' has scale (${scale2D.x}, ${scale2D.y}). ` +
                    'Size changes to RigidBody2D will be overridden by the physics engine when running. ' +
                    'Change the size in its children collision shapes instead.');
            }
        }
        return diagnostics;
    }
    return {
        meta: {
            name: `valid-${prefix}`,
            description: `Validates ${type} resource references, collision shapes, mass values, and physics configuration`,
            category: 'validation',
            applicableNodeTypeMatcher: (nodeType) => descendsFromClass(nodeType, type),
            emits: armEmits(arms),
        },
        check,
    };
}
//# sourceMappingURL=rigidBodyLinterRule.js.map